import React from 'react';
import { Flex, GoodsCarousel, type GoodsData, Headline } from '@banhala/ui-kit';
import { useAddLikeGoods, useLikeMap } from 'shared/src/hooks';
import { useLogDisplayGoods } from '../../../../../hooks/useLogDisplayGoods';
import {
  bridge,
  isNotNil,
  LOG_ANALYTICS_ALL_CHANNELS,
  useNavigator,
} from 'shared/src/utils';
import type { LogAnalyticsEventParams } from 'shared/src/utils/bridge/logAnalyticsEvent/types';
import { useRemoveLikeGoods } from '../../../queries/useRemoveLikeGoods';
import type {
  AddCartRecommendItem,
  GoodsLikeRecommendItem,
} from 'shared/src/api';
import { AdAction } from 'shared/src/utils/eventLogger/adLogger/types';

export const GoodsCarouselItem: React.FC<{
  isLogEnabled: boolean;
  carousel: {
    item_list: (
      | (GoodsData & AddCartRecommendItem)
      | (GoodsData & GoodsLikeRecommendItem)
    )[];
    title: string;
  };
}> = props => {
  const { logDisplayGoods } = useLogDisplayGoods({
    enabled: props.isLogEnabled,
  });

  const { likeMap, setLikeMap } = useLikeMap(
    props.carousel.item_list.flat(1).reduce((acc, val) => {
      return {
        ...acc,
        [val.item.sno]: val.item.like.is_liked,
      };
    }, {}) ?? {},
  );

  const { addLikeGoods } = useAddLikeGoods({
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen: 'GOODS_DETAIL',
  });
  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const { navigator } = useNavigator();

  return (
    <Flex>
      <Headline
        headline={props.carousel.title}
        size={'medium'}
        topCompact={true}
      />
      <Flex>
        <GoodsCarousel
          list={props.carousel.item_list}
          onPressItem={async (pressed, index) => {
            const { item, logging } = pressed;
            bridge.logAnalyticsEvent({
              name: 'CLICK_GOODS',
              ably_only_log: logging.ably_only_log,
              analytics: {
                SCREEN: 'GOODS_DETAIL',
                ...logging.analytics,
                INDEX: index,
              },
            });
            if (isNotNil(item.ad)) {
              bridge.logAdEvent({ ad: item.ad, action: AdAction.CLICK });
            }
            navigator.moveToGoods({
              sno: item.sno,
              firstPageRendering: item.first_page_rendering,
              ad: item.ad,
            });
          }}
          likeMap={likeMap}
          onPressLike={async (pressed, index) => {
            const { item, logging } = pressed;

            const analytics: LogAnalyticsEventParams['analytics'] = {
              SCREEN: 'GOODS_DETAIL',
              ...logging.analytics,
              INDEX: index,
            };

            if (!likeMap[item.sno]) {
              bridge.logAnalyticsEvent({
                name: 'CLICK_LIKE_GOODS',
                ably_only_log: logging.ably_only_log,
                channel: LOG_ANALYTICS_ALL_CHANNELS,
                analytics,
              });
              if (isNotNil(item.ad)) {
                bridge.logAdEvent({ ad: item.ad, action: AdAction.LIKE });
              }
              // 좋아요 누르기
              addLikeGoods(item.sno, {
                SCREEN: 'GOODS_DETAIL',
              });
              return;
            }

            bridge.logAnalyticsEvent({
              name: 'CLICK_UNLIKE_GOODS',
              ably_only_log: logging.ably_only_log,
              analytics,
            });

            if (isNotNil(item.ad)) {
              bridge.logAdEvent({ ad: item.ad, action: AdAction.UNLIKE });
            }

            await mutateRemoveLikeGoods({
              sno: item.sno,
            });

            //해제하기
            bridge.likeGoods({
              goods_sno: item.sno,
              is_liked: false,
            });
            setLikeMap(prevState => ({
              ...prevState,
              [item.sno]: false,
            }));
          }}
          size={'small'}
          onViewableItemsChanged={logDisplayGoods}
        />
      </Flex>
    </Flex>
  );
};
